import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import {
  closeMenuItemAndGoToMenu,
  handleFormItemSubmit,
  fetchMenuItemEntity
} from 'bento-ordering/menus/actions/menuItem';
import {
  getMenuItemImage,
  getMenuItem,
  getIsDoneFetchingMenuItemsDetail
} from 'bento-ordering/menus/reducers/menuItemsDetail';
import { trackAddToCart } from 'bento-ordering/base/actions/tracking';
import { toggleAlertStatus } from 'bento-ordering/base/actions/alertStatus';

import { ADD_TO_CART } from 'constants/menu/ctaName';
import { MENU_ITEM_PROPTYPES } from 'bento-ordering/base/constants/propTypes';
import { MENU_ITEMS_DETAIL } from 'bento-ordering/menus/constants/entities';
import { NO_SET_ADDRESS } from 'bento-ordering/base/constants/orderAlerts';

import MenuItemDetailForm from 'bento-ordering/menus/containers/MenuItemDetailForm';
import ModalCloseButton from 'bento-ordering/base/ui_components/ModalCloseButton';
import withLoading from 'bento-ordering/base/hoc/withLoading';
import { areFulfillmentDeliveryOptionsSetSelector } from 'bento-ordering/cart/selectors';
import { productPreviewHelperSelectorMenuItem } from 'bento-ordering/menus/reducers/productPreview';
import { isLocationClosedSelector } from 'bento-ordering/locations/selectors';
import useGetModifierGroups from 'hooks/useGetModifierGroups';
import { useWouldExceedSpendingLimit } from 'bento-ordering/group_ordering/hooks/index';
import { openNotificationModal } from 'bento-ordering/base/actions/notificationModals';
import { MENU_ITEM_NOT_ON_CURRENT_MENU } from 'bento-ordering/cart/constants/notificationTypes';

const MenuItemDetail = ({
  selectedLocationIsClosed,
  orderMaximum,
  menuItemImage,
  description,
  name,
  variants,
  id: productId,
  slug: productSlug,
  menuItemPreviewHelper,
  cart,
  modifier_groups,
  match: {
    params: { menuItemSlug }
  },
  isDoneFetchingMenuItem,
  // DIETARY NEEDS + ALLERGENS
  contains_sugar,
  cooked_to_order,
  corn,
  corn_free,
  dairy,
  dairy_free,
  dairy_free_option,
  egg,
  egg_free,
  fish,
  fish_free,
  gluten,
  gluten_free,
  halal,
  keto,
  keto_option,
  kosher,
  kosher_option,
  low_fodmap,
  low_sodium,
  non_alcoholic,
  nuts,
  nuts_free,
  organic,
  organic_option,
  paleo,
  paleo_option,
  peanut,
  peanut_free,
  plant_based,
  plant_based_option,
  raw,
  raw_egg,
  sesame,
  sesame_free,
  shellfish,
  shellfish_free,
  soy,
  soy_free,
  spicy,
  spicy_hot,
  spicy_medium,
  spicy_mild,
  sugar_free,
  tree_nut,
  tree_nut_free,
  uncooked,
  undercooked,
  vegan,
  vegan_option,
  vegetarian,
  vegetarian_option,
  wheat,
  wheat_free
}) => {
  const dispatch = useDispatch();
  const modifierGroups = useGetModifierGroups({
    variants,
    modifier_groups
  });
  const {
    hasExceededSpendingLimit,
    spendingLimit
  } = useWouldExceedSpendingLimit();

  useEffect(() => {
    if (!productId && isDoneFetchingMenuItem) {
      dispatch(openNotificationModal(MENU_ITEM_NOT_ON_CURRENT_MENU));
      dispatch(closeMenuItemAndGoToMenu());
    }
  }, [dispatch, productId, isDoneFetchingMenuItem]);

  useEffect(() => {
    if (!isEmpty(menuItemSlug)) {
      dispatch(fetchMenuItemEntity(menuItemSlug));
    }
  }, [dispatch, menuItemSlug]);

  const closeModal = () => {
    dispatch(closeMenuItemAndGoToMenu());
  };

  const handleAddToCart = (values, props) => {
    const latestItemQuantity = values.quantityInput || 1;
    const areFulfillmentDeliveryOptionsSet = areFulfillmentDeliveryOptionsSetSelector(
      cart
    );

    if (!areFulfillmentDeliveryOptionsSet) {
      dispatch(toggleAlertStatus(NO_SET_ADDRESS, { latestItemQuantity }));
    }

    dispatch(trackAddToCart(latestItemQuantity));

    return handleFormItemSubmit(values, props);
  };

  const menuItemDescriptionProps = {
    description,
    name,
    // DIETARY NEEDS + ALLERGENS
    contains_sugar,
    cooked_to_order,
    corn,
    corn_free,
    dairy,
    dairy_free,
    dairy_free_option,
    egg,
    egg_free,
    fish,
    fish_free,
    gluten,
    gluten_free,
    halal,
    keto,
    keto_option,
    kosher,
    kosher_option,
    low_fodmap,
    low_sodium,
    non_alcoholic,
    nuts,
    nuts_free,
    organic,
    organic_option,
    paleo,
    paleo_option,
    peanut,
    peanut_free,
    plant_based,
    plant_based_option,
    raw,
    raw_egg,
    sesame,
    sesame_free,
    shellfish,
    shellfish_free,
    soy,
    soy_free,
    spicy,
    spicy_hot,
    spicy_medium,
    spicy_mild,
    sugar_free,
    tree_nut,
    tree_nut_free,
    uncooked,
    undercooked,
    vegan,
    vegan_option,
    vegetarian,
    vegetarian_option,
    wheat,
    wheat_free
  };

  if (selectedLocationIsClosed) {
    return <Redirect to="/" />;
  }

  return (
    <React.Fragment>
      <ModalCloseButton
        autoFocus
        handleClick={closeModal}
        isWhite={!!menuItemImage.url}
      />
      <MenuItemDetailForm
        productId={productId}
        productName={name}
        productSlug={productSlug}
        defaultCtaName={ADD_TO_CART}
        handleFormItemSubmit={(values, props) => handleAddToCart(values, props)}
        orderMaximum={orderMaximum}
        menuItemImage={menuItemImage}
        menuItemDescriptionProps={menuItemDescriptionProps}
        modifierGroups={modifierGroups}
        variants={variants}
        {...menuItemPreviewHelper}
        spendingLimit={spendingLimit}
        hasExceededSpendingLimit={hasExceededSpendingLimit}
      />
    </React.Fragment>
  );
};

MenuItemDetail.propTypes = {
  selectedLocationIsClosed: PropTypes.bool,
  ...MENU_ITEM_PROPTYPES,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      menuItemSlug: PropTypes.string
    })
  }),
  orderMaximum: PropTypes.string,
  menuItemImage: PropTypes.object,
  modifier_groups: PropTypes.array
};

MenuItemDetail.defaultProps = {
  variants: [],
  modifier_groups: []
};

const mapStateToProps = (state, ownProps) => {
  const { menuItemSlug } = ownProps.match.params;
  const menuItem = getMenuItem(state, menuItemSlug);
  const { cart, restaurantLocations } = state;
  const orderMaximum = get(cart, 'max_order_amount', 0);
  const selectedLocationId = cart.online_ordering_location;
  const isDoneFetchingMenuItem = getIsDoneFetchingMenuItemsDetail(state);

  return {
    ...menuItem,
    cart,
    menuItemImage: getMenuItemImage(menuItem),
    selectedLocationIsClosed: isLocationClosedSelector(
      restaurantLocations,
      selectedLocationId
    ),
    orderMaximum,
    menuItemPreviewHelper: productPreviewHelperSelectorMenuItem(state),
    isDoneFetchingMenuItem
  };
};

export default withRouter(
  connect(mapStateToProps)(withLoading(MenuItemDetail, MENU_ITEMS_DETAIL))
);
